/*
  Wunschliste im Browser-Speicher, optional am Kundenkonto gespiegelt.
  Stellt window.KGroup.wishlist (read, write, has, toggle) und den Zaehler
  <kg-wishlist-count> bereit. Elemente mit data-wishlist-toggle="<handle>"
  schalten den Eintrag um (Klasse is-active, aria-pressed).

  Konto-Abgleich: <kg-wishlist-sync data-endpoint="..."> ist nur vorhanden,
  wenn der Kunde eingeloggt ist und die Theme-Einstellung aktiv ist. Beim
  Laden werden gespeicherte und lokale Liste vereint, danach schreibt jede
  Aenderung die ganze Liste an die angegebene Adresse. Merken und Entfernen
  gehen zusaetzlich als Ereignis an den Newsletter-Dienst.
*/

const STORAGE_KEY = 'kgroup:wishlist';

const sync = {
  endpoint: null,
  timer: null,
  pending: null,

  get enabled() {
    return Boolean(this.endpoint);
  },

  async pull() {
    if (!this.enabled) return;
    try {
      const response = await fetch(this.endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const remote = (await response.json()).items;
      if (!Array.isArray(remote)) return;
      const local = wishlist.read();
      const merged = [...remote, ...local.filter((handle) => !remote.includes(handle))];
      wishlist.write(merged, { silent: true });
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: merged } }));
      if (merged.length !== remote.length) this.push(merged);
    } catch {
      /* Konto nicht erreichbar, lokale Liste gilt */
    }
  },

  push(items, event) {
    if (!this.enabled) return;
    this.pending = { items, event: event || this.pending?.event || null };
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.flush(), 400);
  },

  async flush() {
    const payload = this.pending;
    this.pending = null;
    if (!payload) return;
    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      /* naechste Aenderung versucht es erneut */
    }
  },
};

const wishlist = {
  read() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  },
  write(items, options = {}) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* Speicher nicht verfuegbar, Zustand gilt nur fuer diese Seite */
    }
    if (!options.silent) {
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
      sync.push(items, options.event);
    }
  },
  has(handle) {
    return this.read().includes(handle);
  },
  toggle(handle, meta = {}) {
    const items = this.read();
    const index = items.indexOf(handle);
    if (index === -1) items.push(handle);
    else items.splice(index, 1);
    this.write(items, { event: { type: index === -1 ? 'add' : 'remove', handle, ...meta } });
    return index === -1;
  },
};

window.KGroup = window.KGroup || {};
window.KGroup.wishlist = wishlist;

class KgWishlistCount extends HTMLElement {
  connectedCallback() {
    this.render = this.render.bind(this);
    this.onStorage = (event) => {
      if (event.key === STORAGE_KEY) this.render();
    };
    document.addEventListener('wishlist:updated', this.render);
    window.addEventListener('storage', this.onStorage);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.render);
    window.removeEventListener('storage', this.onStorage);
  }

  render() {
    const count = wishlist.read().length;
    this.textContent = count;
    this.dataset.count = count;
    this.hidden = count === 0;
    this.stups(count);
  }

  /*
    Kurzer Stupser, wenn sich die Zahl aendert. Nicht beim ersten Zeichnen und nicht, wenn die
    Zahl gleich bleibt — sonst huepfte die Blase bei jedem Seitenaufruf. Anders als bei den
    Neuigkeiten wird nicht aus dem Nichts eingeblendet, die Blase ist ja schon da.

    Dasselbe steht im jeweils anderen Zaehler (assets/header.js und assets/wishlist.js). Ein
    gemeinsamer Import schied aus: Relative Modulpfade im Theme tragen keine Version, Browser
    behalten dann die alte Datei im Zwischenspeicher.
  */
  stups(count) {
    const vorher = this.letzterStand;
    this.letzterStand = count;
    if (vorher === undefined || vorher === count || count === 0) return;
    this.classList.remove('is-bump');
    /* Lesen erzwingt den Umbruch, sonst startet dieselbe Animation nicht neu */
    void this.offsetWidth;
    this.classList.add('is-bump');
    this.addEventListener('animationend', () => this.classList.remove('is-bump'), { once: true });
  }
}

customElements.define('kg-wishlist-count', KgWishlistCount);

class KgWishlistSync extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.endpoint) return;
    sync.endpoint = this.dataset.endpoint;
    sync.pull();
  }
}

customElements.define('kg-wishlist-sync', KgWishlistSync);

const syncToggles = () => {
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const active = wishlist.has(button.dataset.wishlistToggle);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  const { wishlistToggle, wishlistTitle, wishlistUrl, wishlistImage, wishlistPrice } = button.dataset;
  wishlist.toggle(wishlistToggle, { title: wishlistTitle || '', url: wishlistUrl || '', image: wishlistImage || '', price: wishlistPrice || '' });
});
document.addEventListener('wishlist:updated', syncToggles);
document.addEventListener('DOMContentLoaded', syncToggles);
syncToggles();
